import React, { Component } from 'react';
import { IDriverDebtProps } from '../../types/propTypes/propTypes';
import { IDriverDebtContentState } from '../../types/stateTypes/stateTypes';
import Select from 'react-select';
import { Common } from '../../utils/constants';
import ability, { Can } from '../../ability';
import CommonService from '../../services/common/common-service';
import StandardResponse from '../../models/standard-response';
import _ from 'lodash';
import { toast } from 'react-toastify';
import DriverService from '../../services/driver/driver-service';
import { ILookupDetailList, ILookupDetailRequest } from '../../models/common-models';
import moment from 'moment';
import Table from '../../components/table/Table';
import { IPastLetterDetails, IRunHistoryDetails, IRunHistoryList } from '../../models/driver-debt';
import RegenerateLetterModal from './RegenerateLetterModal';
import { copyText, regionDateAndTime } from '../../utils';
import images from '../../assets/images';

class DriverDebtContent extends Component<IDriverDebtProps, IDriverDebtContentState> {
    private commonService: CommonService;
    private driverService: DriverService;
    constructor(props: IDriverDebtProps) {
        super(props);
        this.state = {
            s1Days: 0,
            s2Days: 0,
            s1Letters: [],
            s2Letters: [],
            s1Letter: null,
            s2Letter: null,
            status: { STRIKE_ONE: '', STRIKE_TWO: '' },
            runHistoryColumns: [],
            runHistoryList: [],
            totalCount: 0,
            pastLetterColumns: [],
            pastLettersList: [],
            plTotalCount: 0,
            regenerateLetterModal: {
                showModal: false,
                runHistoryDetails: null,
            },
        };
        this.commonService = new CommonService();
        this.driverService = new DriverService();
    }

    async componentDidMount() {
        const { t } = this.props;

        if (ability.can(Common.Actions.GENERATE_CHASE_REPORT, Common.Modules.DRIVERS.DRIVER_DEBT)) {
            this.getConfigData(Common.LookupCode.BAD_DEBT_STRIKE_ONE_DEBT_AGE).then((res) =>
                this.setState({ s1Days: +res }),
            );
            this.getConfigData(Common.LookupCode.BAD_DEBT_STRIKE_TWO_DEBT_AGE).then((res) =>
                this.setState({ s2Days: +res }),
            );
            this.getStatus();
        }

        if (
            ability.can(Common.Actions.GENERATE_CHASE_REPORT, Common.Modules.DRIVERS.DRIVER_DEBT) ||
            ability.can(Common.Actions.REGENERATE_DEBT_LETTER, Common.Modules.DRIVERS.DRIVER_DEBT)
        ) {
            this.getTemplates();
        }

        const header = [
            {
                label: t('driverDebt.columns.run'),
                id: 'id',
                sortable: false,
                isVisible: true,
                cell: (row: IRunHistoryDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.id as any}>
                            {row.id}
                        </span>
                    );
                },
            },
            {
                label: t('driverDebt.columns.driverIDs'),
                id: 'driver_ids',
                sortable: false,
                isVisible: true,
                style: { maxWidth: 250 },
                cell: (row: IRunHistoryDetails) => {
                    return (
                        <span
                            className="d-block text-truncate"
                            title={t<string>('driverDebt.label.copyDriverIds')}
                            style={{ cursor: 'pointer' }}
                            onClick={() => copyText(row.driver_ids)}
                        >
                            {row.driver_ids}
                        </span>
                    );
                },
            },
            {
                label: t('driverDebt.columns.runTimestamp'),
                id: 'creation_date_time',
                sortable: true,
                isVisible: true,
                cell: (row: IRunHistoryDetails) => {
                    if (row.creation_date_time) {
                        return moment(row.creation_date_time).format(regionDateAndTime()?.GENERAL_DATE_TIME_FORMAT);
                    }
                    return null;
                },
            },
            {
                label: t('driverDebt.columns.strikeType'),
                id: 'chase_report_type',
                sortable: true,
                isVisible: true,
                cell: (row: IRunHistoryDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.chase_report_type?.code}>
                            {row.chase_report_type?.code}
                        </span>
                    );
                },
            },
            {
                label: t('driverDebt.columns.initiatedByUser'),
                id: 'initiated_by',
                sortable: true,
                isVisible: true,
                cell: (row: IRunHistoryDetails) => {
                    return (
                        <span
                            className="d-block text-nowrap-normal"
                            title={row.chase_report_generation_history_data[0]?.initiated_by}
                        >
                            {row.chase_report_generation_history_data[0]?.initiated_by}
                        </span>
                    );
                },
            },
            {
                label: t('driverDebt.columns.action'),
                id: 'action',
                sortable: false,
                isVisible: ability.can(Common.Actions.REGENERATE_DEBT_LETTER, Common.Modules.DRIVERS.DRIVER_DEBT),
                style: { width: 80 },
                className: 'text-center',
                cell: (row: IRunHistoryDetails) => {
                    return (
                        <a
                            className="icon pointer"
                            title={t<string>('driverDebt.button.regenerateLetters')}
                            onClick={() => this.manageRegenerateLetterModal(true, row)}
                        >
                            <img
                                src={images.replaceSVG}
                                alt={t<string>('driverDebt.button.regenerateLetters')}
                                className="action-icon-img"
                            />
                        </a>
                    );
                },
            },
        ];

        const pastLettersHeader = [
            {
                label: t('driverDebt.columns.relatedRun'),
                id: 'chase_report_data_id',
                sortable: true,
                isVisible: true,
                cell: (row: IPastLetterDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.chase_report_data_id}>
                            {row.chase_report_data_id}
                        </span>
                    );
                },
            },
            {
                label: t('driverDebt.columns.date'),
                id: 'creation_date_time',
                sortable: true,
                isVisible: true,
                cell: (row: IPastLetterDetails) => {
                    if (row.creation_date_time) {
                        return moment(row.creation_date_time).format(regionDateAndTime()?.GENERAL_DATE_TIME_FORMAT);
                    }
                    return null;
                },
            },
            {
                label: t('driverDebt.columns.file'),
                id: 'file_name',
                sortable: false,
                isVisible: true,
                cell: (row: IPastLetterDetails) => {
                    if (row.file_name) {
                        return (
                            <span
                                className={
                                    ability.can(
                                        Common.Actions.DOWNLOAD_DEBT_LETTER,
                                        Common.Modules.DRIVERS.DRIVER_DEBT,
                                    ) &&
                                    (!row.is_file_deleted_from_s3 || row.is_file_deleted_from_s3 === null)
                                        ? 'd-block text-nowrap-normal click-here'
                                        : 'd-block text-nowrap-normal'
                                }
                                title={this.getFileName(row.file_name)}
                                onClick={() => {
                                    if (
                                        ability.can(
                                            Common.Actions.DOWNLOAD_DEBT_LETTER,
                                            Common.Modules.DRIVERS.DRIVER_DEBT,
                                        ) &&
                                        (!row.is_file_deleted_from_s3 || row.is_file_deleted_from_s3 === null)
                                    )
                                        this.driverService.downloadDriverDebtChaseLetter(row.file_name);
                                }}
                            >
                                {this.getFileName(row.file_name)}
                            </span>
                        );
                    }
                    return (
                        <span
                            className="d-block text-nowrap-normal"
                            title={t<string>('driverDebt.label.reportGenerationInProgress')}
                        >
                            {t('driverDebt.label.reportGenerationInProgress')}
                        </span>
                    );
                },
            },
            {
                label: t('driverDebt.columns.templateUsed'),
                id: 'debtor_letter_name',
                sortable: true,
                isVisible: true,
                cell: (row: IPastLetterDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.debtor_letter_name}>
                            {row.debtor_letter_name}
                        </span>
                    );
                },
            },
            {
                label: t('driverDebt.columns.generatedBy'),
                id: 'initiated_by',
                sortable: true,
                isVisible: true,
                cell: (row: IPastLetterDetails) => {
                    return (
                        <span className="d-block text-nowrap-normal" title={row.initiated_by}>
                            {row.initiated_by}
                        </span>
                    );
                },
            },
        ];

        this.setState(
            {
                runHistoryColumns: header,
                pastLetterColumns: pastLettersHeader,
            },
            async () => {
                const runHistoryList = await this.getRunHistoryList();
                const pastLettersList = await this.getPastLettersList();

                this.setState({
                    runHistoryList,
                    pastLettersList,
                });
            },
        );
    }

    startGeneration = async (type: string) => {
        let template_id = -1;
        if (type === Common.LookupCode.STRIKE_ONE) {
            if (this.state.s1Letter && this.state.s1Letter.id) template_id = this.state.s1Letter.id;
        } else if (type === Common.LookupCode.STRIKE_TWO) {
            if (this.state.s2Letter && this.state.s2Letter.id) template_id = this.state.s2Letter.id;
        }
        if (template_id > -1) {
            await this.driverService
                .generateDriverDebtChaseLetters(type, template_id)
                .then(async (res: StandardResponse<any>) => {
                    if (res.Messages) {
                        toast.success(res.Messages);
                    }
                })
                .catch((m) => {
                    if (m.response && m.response.data && m.response.data.message) {
                        toast.error(m.response.data.message);
                    } else {
                        toast.error(m.toString());
                    }
                });
            this.getStatus();
            const runHistoryList = await this.getRunHistoryList();
            const pastLettersList = await this.getPastLettersList();

            this.setState({
                runHistoryList,
                pastLettersList,
            });
        }
    };

    getStatus = async () => {
        await this.driverService.getDriverDebtLettersGenerationStatus().then((res) => {
            this.setState({ status: res.Data });
        });
    };
    getTemplates = async () => {
        this.driverService.getDriverDebtLetters({ status: [Common.ENABLE] }).then((res) => {
            const s1Letters = res.Data.rows
                .filter((d) => d.driver_debt_report_type.code === Common.LookupCode.STRIKE_ONE)
                .map((item) => {
                    item = Object.assign(item, { label: item.name, value: item.id });
                    return item;
                });
            const s2Letters = res.Data.rows
                .filter((d) => d.driver_debt_report_type.code === Common.LookupCode.STRIKE_TWO)
                .map((item) => {
                    item = Object.assign(item, { label: item.name, value: item.id });
                    return item;
                });
            const s1Letter = res.Data.rows.find(
                (d) => d.driver_debt_report_type.code === Common.LookupCode.STRIKE_ONE && d.is_default === true,
            );
            if (s1Letter) {
                s1Letter.label = s1Letter.name;
                s1Letter.value = s1Letter.id;
            }
            const s2Letter = res.Data.rows.find(
                (d) => d.driver_debt_report_type.code === Common.LookupCode.STRIKE_TWO && d.is_default === true,
            );
            if (s2Letter) {
                s2Letter.label = s2Letter.name;
                s2Letter.value = s2Letter.id;
            }
            this.setState({ s1Letters: s1Letters, s2Letters: s2Letters, s1Letter: s1Letter, s2Letter: s2Letter });
        });
    };

    getLookupDetailList = async () => {
        let lookupList: ILookupDetailList[] = [];
        const filterInput = {
            code: [Common.LookupCode.STRIKE_ONE, Common.LookupCode.STRIKE_TWO],
        } as ILookupDetailRequest;
        await this.commonService
            .getLookupDetailList(filterInput)
            .then(async (res: StandardResponse<ILookupDetailList[]>) => {
                if (_.isArray(res.Data)) {
                    lookupList = res.Data;
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            })
            .finally();
        return lookupList;
    };

    getConfigData = async (code: string) => {
        let configData: Array<any> = [];
        await this.commonService
            .getConfiguration({ code })
            .then(async (res) => {
                configData = res.data;
            })
            .catch((m) => {
                toast.error(m.string());
            });
        if (configData.length > 0 && configData[0] && configData[0].value) {
            return configData[0].value;
        }
        return null;
    };

    getFileName = (filePath: string) => {
        return filePath.split('/')[filePath.split('/').length - 1];
    };

    getRunHistoryList = async () => {
        const { rowsPerPage, offset, sortColumn, sortOrder } = this.props.filterState;
        let runHistoryList: Array<any> = [];
        const filterInput = {};
        Object.assign(filterInput, {
            limit: rowsPerPage,
            offset: offset,
            order: [[sortColumn, sortOrder]],
        });
        await this.driverService
            .getRunHistoryList(filterInput)
            .then(async (res: StandardResponse<IRunHistoryList>) => {
                runHistoryList = res.Data.rows;
                this.setState({ totalCount: res.Data.count });
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });

        return runHistoryList;
    };
    getPastLettersList = async () => {
        const { plRowsPerPage, plOffset, plSortColumn, plSortOrder } = this.props.pastLettersFilterState;
        let pastLettersList: Array<any> = [];
        const filterInput = {};
        Object.assign(filterInput, {
            limit: plRowsPerPage,
            offset: plOffset,
            order: [[plSortColumn, plSortOrder]],
        });
        await this.driverService
            .getPastDriverDebtChaseLetters(filterInput)
            .then(async (res: StandardResponse<IRunHistoryList>) => {
                pastLettersList = res.Data.rows;
                this.setState({ plTotalCount: res.Data.count });
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });

        return pastLettersList;
    };
    handlePageChange = async (currentPage: number, rowsPerPage: number) => {
        const offset = (currentPage - 1) * rowsPerPage;
        this.props.updateFilterState({ currentPage, rowsPerPage, offset }, async () => {
            const runHistoryList = await this.getRunHistoryList();
            this.setState({
                runHistoryList,
            });
        });
    };
    handlePastLettersPageChange = async (plCurrentPage: number, plRowsPerPage: number) => {
        const plOffset = (plCurrentPage - 1) * plRowsPerPage;
        this.props.updatePLFilterState({ plCurrentPage, plRowsPerPage, plOffset }, async () => {
            const pastLettersList = await this.getPastLettersList();
            this.setState({
                pastLettersList,
            });
        });
    };

    handleSorting = async (columnName: string) => {
        const { sortColumn } = this.props.filterState;
        let sortOrder = this.props.filterState.sortOrder;
        if (sortColumn === columnName) {
            sortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        } else {
            sortOrder = 'asc';
        }

        this.props.updateFilterState({ sortOrder, sortColumn: columnName }, async () => {
            const runHistoryList = await this.getRunHistoryList();

            this.setState({
                runHistoryList,
            });
        });
    };

    handlePastLettersSorting = async (columnName: string) => {
        const { plSortColumn } = this.props.pastLettersFilterState;
        let plSortOrder = this.props.pastLettersFilterState.plSortOrder;
        if (plSortColumn === columnName) {
            plSortOrder = plSortOrder === 'desc' ? 'asc' : 'desc';
        } else {
            plSortOrder = 'asc';
        }

        this.props.updatePLFilterState({ plSortOrder, plSortColumn: columnName }, async () => {
            const pastLettersList = await this.getPastLettersList();
            this.setState({
                pastLettersList,
            });
        });
    };
    manageRegenerateLetterModal = async (showModal: boolean, runHistoryDetails: IRunHistoryDetails | null = null) => {
        this.setState({ ...this.state, regenerateLetterModal: { showModal, runHistoryDetails } });
    };

    refreshPastLettersList = async () => {
        const pastLettersList = await this.getPastLettersList();
        this.setState({ pastLettersList });
    };

    render() {
        const { t } = this.props;
        const { rowsPerPage, currentPage, sortColumn, sortOrder } = this.props.filterState;
        const { plRowsPerPage, plCurrentPage, plSortColumn, plSortOrder } = this.props.pastLettersFilterState;

        return (
            <>
                <div className="title row align-items-content justify-content-between pb-3">
                    <div className="col-auto col-sm text-truncate pr-0">
                        <h1 className="text-truncate">{t('driverDebt.header')}</h1>
                    </div>
                </div>
                <div className="card-outer driver-debt-page">
                    <Can I={Common.Actions.GENERATE_CHASE_REPORT} a={Common.Modules.DRIVERS.DRIVER_DEBT}>
                        <div className="pt-3 px-3">
                            {+this.state.s1Days !== 0 && (
                                <div className="row">
                                    <div className="form-group col-md-6 col-lg-4 col-xl-3">
                                        <label>{t('driverDebt.button.strikeOneLetter')}</label>
                                        <Select
                                            name="status"
                                            // className="form-control"
                                            value={this.state.s1Letter}
                                            placeholder={t('driverDebt.placeholder.strikeOneLetter')}
                                            isMulti={false}
                                            options={this.state.s1Letters}
                                            optionText={(opt) => opt.name}
                                            isClearable={false}
                                            onChange={(value) => this.setState({ s1Letter: value })}
                                            noOptionsMessage={() => t('noOptions')}
                                            styles={{
                                                input: (styles) => ({
                                                    ...styles,
                                                    borderRadius: '4px',
                                                    borderColor: '#dadada !important',
                                                    outline: 'none !important',
                                                    boxShadow: 'none !important',
                                                }),
                                            }}
                                        />
                                    </div>
                                    <div className="form-group col-md-auto mt-lg-23p">
                                        <button
                                            type="button"
                                            className="btn btn-primary m-w-170p"
                                            onClick={() => this.startGeneration(Common.LookupCode.STRIKE_ONE)}
                                            disabled={
                                                this.state.status.STRIKE_ONE.status === Common.LookupCode.IN_PROGRESS ||
                                                !this.state.s1Letter ||
                                                (this.state.status.STRIKE_ONE.status === Common.LookupCode.COMPLETED &&
                                                    this.state.status.STRIKE_ONE.last_generated_report
                                                        .creation_date_time &&
                                                    moment(
                                                        this.state.status.STRIKE_ONE.last_generated_report
                                                            .creation_date_time,
                                                    ).isSame(moment(), 'day'))
                                            }
                                            title={t<string>('driverDebt.button.generateStrikeOneLetters')}
                                        >
                                            <span>{t('driverDebt.button.generateStrikeOneLetters')}</span>
                                        </button>
                                        <span className="pl-3">
                                            {this.state.status.STRIKE_ONE.status === Common.LookupCode.IN_PROGRESS &&
                                                t('driverDebt.label.inProgress')}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {+this.state.s2Days !== 0 && (
                                <div className="row">
                                    <div className="form-group col-md-6 col-lg-4 col-xl-3">
                                        <label>{t('driverDebt.button.strikeTwoLetter')}</label>
                                        <Select
                                            name="status"
                                            class="form-control"
                                            value={this.state.s2Letter}
                                            isMulti={false}
                                            options={this.state.s2Letters}
                                            optionText={(opt) => opt.name}
                                            onChange={(value) => this.setState({ s2Letter: value })}
                                            isClearable={false}
                                            noOptionsMessage={() => t('noOptions')}
                                            styles={{
                                                input: (styles) => ({
                                                    ...styles,
                                                    borderRadius: '4px',
                                                    borderColor: '#dadada !important',
                                                    outline: 'none !important',
                                                    boxShadow: 'none !important',
                                                }),
                                            }}
                                            placeholder={t('driverDebt.placeholder.strikeTwoLetter')}
                                        />
                                    </div>
                                    <div className="form-group col-md-auto mt-lg-23p">
                                        <button
                                            type="button"
                                            className="btn btn-primary m-w-170p"
                                            onClick={() => this.startGeneration(Common.LookupCode.STRIKE_TWO)}
                                            disabled={
                                                this.state.status.STRIKE_TWO.status === Common.LookupCode.IN_PROGRESS ||
                                                !this.state.s2Letter ||
                                                (this.state.status.STRIKE_TWO.status === Common.LookupCode.COMPLETED &&
                                                    this.state.status.STRIKE_TWO.last_generated_report
                                                        .creation_date_time &&
                                                    moment(
                                                        this.state.status.STRIKE_TWO.last_generated_report
                                                            .creation_date_time,
                                                    ).isSame(moment(), 'day'))
                                            }
                                            title={t<string>('driverDebt.button.generateStrikeTwoLetters')}
                                        >
                                            <span>{t('driverDebt.button.generateStrikeTwoLetters')}</span>
                                        </button>
                                        <span className="pl-3">
                                            {this.state.status.STRIKE_TWO.status === Common.LookupCode.IN_PROGRESS &&
                                                t('driverDebt.label.inProgress')}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Can>
                    <div className="px-2 pt-2">
                        <span className="debt-grid-header">{t('driverDebt.button.runHistory')}</span>
                    </div>
                    <Table
                        columns={this.state.runHistoryColumns}
                        data={this.state.runHistoryList}
                        rowsPerPage={rowsPerPage}
                        currentPage={currentPage}
                        totalCount={this.state.totalCount}
                        sortColumn={sortColumn}
                        sortOrder={sortOrder}
                        handlePageChange={this.handlePageChange}
                        handleSorting={this.handleSorting}
                        t={this.props.t}
                    />
                    <div className="px-2 mt-2 d-flex justify-content-between">
                        <div className="d-flex align-items-center">
                            <span className="debt-grid-header">{t('driverDebt.button.pastLetters')}</span>
                        </div>
                        <div>
                            <button
                                title={t<string>('driverDebt.button.refreshPastLetters')}
                                type="button"
                                id="refreshPastLetters"
                                className="btn icon-btn pr-0 btn-outline-primary mr-2"
                                onClick={() => this.refreshPastLettersList()}
                            >
                                <img
                                    className="icon sync-icon"
                                    src={images.synchronizeSVG}
                                    alt={t<string>('driverDebt.button.refreshPastLetters')}
                                />
                            </button>
                        </div>
                    </div>
                    <Table
                        columns={this.state.pastLetterColumns}
                        data={this.state.pastLettersList}
                        rowsPerPage={plRowsPerPage}
                        currentPage={plCurrentPage}
                        totalCount={this.state.plTotalCount}
                        sortColumn={plSortColumn}
                        sortOrder={plSortOrder}
                        handlePageChange={this.handlePastLettersPageChange}
                        handleSorting={this.handlePastLettersSorting}
                        t={this.props.t}
                    />
                </div>
                {this.state.regenerateLetterModal.showModal && (
                    <RegenerateLetterModal
                        t={t}
                        showModal={this.state.regenerateLetterModal.showModal}
                        closeModal={() => this.manageRegenerateLetterModal(false)}
                        s1Letters={this.state.s1Letters}
                        s2Letters={this.state.s2Letters}
                        runHistoryDetails={this.state.regenerateLetterModal.runHistoryDetails}
                        refreshPastLettersList={this.refreshPastLettersList}
                    />
                )}
            </>
        );
    }
}
export default DriverDebtContent;
